import { useCallback, useEffect, useMemo, useState } from 'react'

import moment from 'moment'
import { isArray } from 'lodash'
import { useSelector } from 'react-redux'

import Settings from './Settings'
import PlacesHeader from './PlacesHeader'
import QuotaFormModalProvider from '../../../Providers/Forms/QuotaForm/QuotaFormModalProvider'
import { TimePeriod, PeriodTypes } from '../../../utils/timePeriods'
import { useLocalStorage } from '../../../hooks/UseLocalStorage'
import LOCAL_STORAGE_KEYS, { localStorage } from '../../../utils/localStorage'
import { SchoolQuotasSettings } from '../../../Components/antd/Tables/DragTable/OverlaySettings'
import { getDifference, getQuotasMap, getStorageKey, QuotasMap, reduceBySector, updateQuotaByActions } from '../../../Components/shared/Quotas/utils'
import { selectUser } from '../../../reducers/UserReducer'
import { getAllQuotas, SerializationContexts as QuotaContexts } from '../../../utils/api/quota'
import { API_DATE, arePeriodsOverlapping } from '../../../utils/momentjs'
import PlacesTable from './PlacesTable'
import { getAllStudentPathways, SerializationContexts as PathwayContexts } from '../../../utils/api/studentPathway'
import { QuotaFormModes } from '../../../Providers/Forms/QuotaForm/QuotaForm'
import { getIdOrNull, isNonEmptyArray, isObject } from '../../../utils'
import useRequest from '../../../hooks/useRequest'
import { getSchoolPeriods } from '../../../utils/api/schoolPeriods'
import { OrderBy, OrderDirections } from '../../../utils/constants'
import { SchoolPeriod } from '../../../utils/entities/schoolPeriod'
import HighlightedColumnsProvider from '../../../Providers/Quotas/HighlightedColumnsProvider'
import QuotaToolBar from '../../institution/Quotas/SchoolQuotasView/QuotaToolBar'
import { getBySectors } from '../../../utils/api/sectionGroup'
import { Quota } from '../../../utils/entities/quota'
import useQuota from '../../../utils/api/hooks/useQuota'
import { findSection } from '../../../utils/entities/sectionGroup'
import { StudentPathway } from '../../../utils/entities/studentPathway'

import '../../../assets/quota-allocation.scss'

const { NAMES, INSTITUTION, LABELS } = QuotaContexts
const { SECTION, SECTOR } = PathwayContexts
const STEP = 10

const PlacesView = () => {
  const user = useSelector(selectUser)

  const [period, setPeriod] = useState(new TimePeriod(moment(), 26, PeriodTypes.WEEK))
  const [overlaySettings, setOverlaySettings] = useLocalStorage(LOCAL_STORAGE_KEYS.PLACES_TABLE.OVERLAY_SETTINGS, new SchoolQuotasSettings(), (value) => new SchoolQuotasSettings(value))
  const [studentPathways, setStudentPathways] = useState({})
  const [rawQuotasMap, setRawQuotasMap] = useState({})
  const [sectors, setSectors] = useState([])
  const [activeSectors, setActiveSectors] = useState([])
  const [page, setPage] = useState(1)
  const [selectedQuotas, setSelectedQuotas] = useState([])
  const [sectionGroups, setSectionGroups] = useState({})
  const [updateLoading, setUpdateLoading] = useState({})

  const holidays = useRequest({
    request: getSchoolPeriods,
    parameters: useMemo(() => ({
      filters: { ...period.getDates(API_DATE), school: getIdOrNull(user.school) },
      order: { by: OrderBy.START_DATE, direction: OrderDirections.ASC }
    }), [user, user.school, period]),
    select: useCallback(holidays => holidays.map(holiday => getIdOrNull(holiday) ? new SchoolPeriod(holiday) : null), [])
  })

  const loadSectors = useCallback((sectors, period) => {
    const startDate = period.startDate.format(API_DATE)
    const endDate = period.endDate.format(API_DATE)
    const sectorIds = sectors.map(s => s.id)

    if (isNonEmptyArray(sectors)) {
      getAllQuotas(user, { contexts: [NAMES, INSTITUTION, LABELS], startDate, endDate, sectors: sectorIds }).then(({ data }) => {
        const quotas = data.map(q => new Quota(q))

        setRawQuotasMap(quotasMap => ({ ...quotasMap, ...reduceBySector(quotas) }))
      })

      getAllStudentPathways(user, { contexts: [SECTION, SECTOR], sectors: sectorIds }).then(({ data }) => {
        const pathways = data.map(pathway => new StudentPathway(pathway))

        setStudentPathways(studentPathways => ({ ...studentPathways, ...reduceBySector(pathways) }))
      })

      getBySectors(user, { sectors: sectorIds }).then(({ data }) => setSectionGroups(prev => ({ ...prev, ...data })))
    }
  }, [user])

  const quotasMap = useMemo(() => Object.keys(rawQuotasMap).reduce((acc, key) => {
    acc[key] = getQuotasMap(rawQuotasMap[key])

    return acc
  }, {}), [rawQuotasMap])

  const quotasToUpdate = useMemo(() => Object.keys(quotasMap).reduce((acc, key) => {
    const sectorSectionsGroup = sectionGroups[key] ?? []
    acc.sectorKeys.push(key)

    if (isNonEmptyArray(sectorSectionsGroup) && !updateLoading[key]) {
      quotasMap[key].desideratas.filter(q => isObject(q.section)).forEach(q => {
        const sectionGroup = sectorSectionsGroup.find(g => findSection(g, q.section))

        if (isObject(sectionGroup) && !acc.quotas.some(quota => quota.id === q.id)) {
          acc.quotas.push({ ...q, sectionGroup, section: null })
        }
      })
    }

    return acc
  }, { sectorKeys: [], quotas: [] }), [quotasMap, sectionGroups, updateLoading])

  const config = useMemo(() => ({ overlay: overlaySettings }), [overlaySettings])

  const handleSectorsChange = useCallback((modifiedSectors, add) => {
    let newSectors = []

    if (isArray(modifiedSectors)) {
      const newActiveSectors = modifiedSectors.slice(0, STEP)
      newSectors = modifiedSectors

      setActiveSectors(newActiveSectors)
      setPage(1)

      loadSectors(newActiveSectors, period)
      setSelectedQuotas([])
    } else if (add) {
      newSectors = [...sectors, modifiedSectors]

      if (page * STEP >= newSectors.length) {
        setActiveSectors(newSectors)
        loadSectors([modifiedSectors], period)
      }
    } else {
      newSectors = sectors.filter(s => getIdOrNull(s) !== getIdOrNull(modifiedSectors))

      const newActiveSectors = newSectors.slice(0, page * STEP)

      setActiveSectors(newActiveSectors)

      const diff = getDifference(newActiveSectors, activeSectors)

      if (diff.length > 0) {
        loadSectors(diff, period)
      }

      setSelectedQuotas(current => current.filter(q => q.sector !== null && getIdOrNull(q.sector) !== getIdOrNull(modifiedSectors)))
    }

    setSectors(newSectors)
  }, [activeSectors, sectors, page, loadSectors, period])

  const handleQuotaChange = useCallback((...params) => {
    const { data: newQuotas, isDeleteAction } = updateQuotaByActions(
      { ...rawQuotasMap },
      params,
      period,
      quota => getStorageKey(quota, LOCAL_STORAGE_KEYS.PLACES_TABLE.QUOTAS_MATRIX),
      quota => getIdOrNull(quota.sector) ?? -1
    )

    setRawQuotasMap(newQuotas)

    if (isDeleteAction) {
      setSelectedQuotas([])
    }
  }, [rawQuotasMap])

  const quotaHandler = useQuota({ callback: handleQuotaChange })

  useEffect(() => {
    const availableQuotas = quotasToUpdate.quotas.filter(q => !updateLoading[q.sector.id])

    if (isNonEmptyArray(availableQuotas)) {
      setUpdateLoading(prev => {
        const newLoading = { ...prev }

        quotasToUpdate.sectorKeys.forEach(key => {
          newLoading[key] = true
        })

        return newLoading
      })

      quotaHandler.update(availableQuotas.map(q => new Quota(q).toRequestBody(true))).then(() => {
        setUpdateLoading(prev => {
          const newLoading = { ...prev }

          quotasToUpdate.sectorKeys.forEach(key => {
            newLoading[key] = false
          })

          return newLoading
        })
      })
    }
  }, [quotasToUpdate, quotaHandler.update])

  const handleScroll = ({ target }) => {
    if (target.scrollTop + target.offsetHeight >= (target.scrollHeight - 20) && (page * STEP) < sectors.length) {
      setPage(page + 1)

      const sectorsToBeLoaded = sectors.slice(page * STEP, (page + 1) * STEP)

      setActiveSectors(current => [...current, ...sectorsToBeLoaded])
      loadSectors(sectorsToBeLoaded, period)
    }
  }

  const handleDateChange = date => {
    const newPeriod = new TimePeriod(date, period.quantity, period.type)
    const newActiveSectors = sectors.slice(0, STEP)

    setPage(1)
    setActiveSectors(newActiveSectors)
    setPeriod(newPeriod)
    loadSectors(newActiveSectors, newPeriod)
    setSelectedQuotas(current => current.filter(q => arePeriodsOverlapping(newPeriod, q)))
  }

  const handlePeriodChange = quantity => {
    const newPeriod = new TimePeriod(period.startDate, quantity, PeriodTypes.WEEK)
    const newActiveSectors = sectors.slice(0, STEP)

    setPage(1)
    setActiveSectors(newActiveSectors)
    setPeriod(newPeriod)
    loadSectors(newActiveSectors, newPeriod)
    setSelectedQuotas(current => current.filter(q => arePeriodsOverlapping(newPeriod, q)))
  }

  const handleResetGravity = useCallback(() => {
    if (activeSectors.length > 0) {
      activeSectors.forEach(s => {
        const storageKey = getStorageKey({ sector: s }, LOCAL_STORAGE_KEYS.PLACES_TABLE.QUOTAS_MATRIX)

        quotasMap[s.id].quotas.forEach(q => {
          localStorage.removeValue(storageKey, q.id)
        })
        quotasMap[s.id].desideratas.forEach(q => {
          localStorage.removeValue(storageKey, q.id)
        })
      })

      setRawQuotasMap(rawMap => ({ ...rawMap }))
    }
  }, [activeSectors, quotasMap])

  return (
    <div className='places-view-container flex-row h-100 w-100'>
      <QuotaFormModalProvider
        sectionGroups={sectionGroups}
        sectors={activeSectors}
        studentPathways={studentPathways}
        mode={QuotaFormModes.DESIDERATA}
      >
        <div className='settings-container mr-8'>
          <Settings onSectorsChange={handleSectorsChange} />
        </div>
        <div className='places-tables-container flex-column h-100'>
          <div className='places-tables-header h-5 w-100 mb-8'>
            <PlacesHeader
              addDisabled={activeSectors.length === 0}
              period={period}
              overlaySettings={overlaySettings}
              onDateChange={handleDateChange}
              onOverlaySettingsChange={(setting, isSelected) => setOverlaySettings(current => current.clone({ [setting]: isSelected }))}
              onPeriodChange={handlePeriodChange}
              onQuotaChange={handleQuotaChange}
              onResetGravity={handleResetGravity}
            />
          </div>
          <div className='places-tables-content-container h-95 w-100 scroll-y' onScroll={handleScroll}>
            <HighlightedColumnsProvider>
              {activeSectors.map((s) => (
                <div key={s.id} className='flex-column w-100 pd-btm-12'>
                  <h3 style={{ margin: '0' }}>{s.name}</h3>
                  <PlacesTable
                    config={config}
                    period={period}
                    sectionGroups={sectionGroups[s.id] ?? []}
                    holidayPeriods={holidays.data}
                    quotasMap={quotasMap[s.id] ?? new QuotasMap()}
                    seed={{ sector: s, school: user.school, isDesiderata: true }}
                    selectedData={selectedQuotas}
                    studentPathways={studentPathways[s.id] ?? []}
                    onChange={handleQuotaChange}
                    onSelectData={setSelectedQuotas}
                  />
                </div>
              ))}
            </HighlightedColumnsProvider>
          </div>
          {isNonEmptyArray(selectedQuotas) && (
            <QuotaToolBar
              selectedQuotas={selectedQuotas.map(q => new Quota(q.quota))}
              onChange={handleQuotaChange}
              onUnselect={() => setSelectedQuotas([])}
            />
          )}
        </div>
      </QuotaFormModalProvider>
    </div>
  )
}

export default PlacesView
